import React from "react";
import {
  Grid, TextField, Box, Autocomplete, Switch,
  FormControlLabel, Button, IconButton, Typography, Divider, MenuItem, Alert, Chip,
} from "@mui/material";
import { Add, Delete } from "@mui/icons-material";
import { deleteVariantAttribute } from "../functions";

const MOVEMENT_TYPES = ["ENTRADA", "SALIDA", "AJUSTE"];

// Opciones predefinidas para el nombre del atributo
const ATTRIBUTE_NAME_OPTIONS = ["Color", "Medida", "Tamaño", "Peso", "Descripción"];

// Normaliza texto: quita acentos y convierte a minúsculas para comparación
const normalize = (str) =>
  String(str ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

// Normaliza para BÚSQUEDA: además reemplaza puntuación por espacios
// (así "Nombre (COD-1)" y "nombre cod 1" coinciden aunque se pegue el texto completo)
const normalizeSearch = (str) =>
  normalize(str)
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

// Filtro de insumos: parcial, sin acentos, multi-palabra y priorizando el nombre
const filterInsumosOptions = (options, { inputValue }) => {
  const needle = normalizeSearch(inputValue);
  if (!needle) return options;
  const needleTokens = needle.split(/\s+/).filter(Boolean);
  return options
    .map((o) => {
      const nombre = normalizeSearch(o.nombre);
      const codigo = normalizeSearch(o.codigo || "");
      const haystack = `${nombre} ${codigo}`.trim();
      const nombreTokens = nombre.split(/\s+/).filter(Boolean);

      // a) Lo escrito está contenido en el insumo (búsqueda parcial mientras escribe)
      const needleInHaystack = needleTokens.every((t) => haystack.includes(t));
      // b) El nombre del insumo está contenido en lo pegado (pegar el dato completo,
      //    aunque traiga texto extra como SKU, stock, etc.)
      const nombreInNeedle =
        nombreTokens.length > 0 && nombreTokens.every((t) => needle.includes(t));

      if (!needleInHaystack && !nombreInNeedle) return null;
      // Prioridad: coincidencia por nombre primero
      const score = nombre.includes(needle) || nombreInNeedle ? 0 : 1;
      return { o, score };
    })
    .filter(Boolean)
    .sort((a, b) => a.score - b.score)
    .map((s) => s.o);
};

// Obtiene el valor de un atributo por nombre (Color, Medida, Tamaño...)
const getAttrValue = (variant, name) => {
  const attr = (variant?.attributes || []).find(
    (a) => normalize(a.attributeName) === normalize(name)
  );
  return attr?.attributeValue || "";
};

// Texto principal de la variante: Color • Medida (con respaldos)
const getVariantPrimaryLabel = (variant) => {
  const color = getAttrValue(variant, "Color");
  const medida = getAttrValue(variant, "Medida") || getAttrValue(variant, "Tamaño");
  const parts = [color, medida].filter(Boolean);
  if (parts.length) return parts.join(" • ");
  const others = (variant?.attributes || [])
    .map((a) => a.attributeValue)
    .filter(Boolean);
  if (others.length) return others.join(" • ");
  return variant?.name || `SKU: ${variant?.sku || "-"}`;
};

// Filtro de variantes: buscable por color, medida, SKU y nombre
const filterVariantsOptions = (options, { inputValue }) => {
  const needle = normalizeSearch(inputValue);
  if (!needle) return options;
  const needleTokens = needle.split(/\s+/).filter(Boolean);
  return options.filter((v) => {
    const attrsText = (v.attributes || [])
      .map((a) => `${a.attributeName} ${a.attributeValue}`)
      .join(" ");
    const haystack = normalizeSearch(`${v.name || ""} ${v.sku || ""} ${attrsText}`);
    const haystackTokens = haystack.split(/\s+/).filter(Boolean);

    // a) Lo escrito está contenido en la variante (búsqueda parcial)
    const needleInHaystack = needleTokens.every((t) => haystack.includes(t));
    // b) La variante está contenida en lo pegado (pegar el dato completo)
    const haystackInNeedle =
      haystackTokens.length > 0 && haystackTokens.every((t) => needle.includes(t));

    return needleInHaystack || haystackInNeedle;
  });
};

const MOVEMENT_COLORS = { ENTRADA: "success", SALIDA: "error", AJUSTE: "warning" };

const StackInsumosFormFields = ({
  formik, insumos, computedStock = 0, baseStock = 0,
  mode, existingVariants = [], selectedVariant, onVariantSelect, onInsumoChange,
}) => {
  const selectedInsumo =
    insumos?.find((i) => i.id === formik.values.insumoId) || null;

  const handleAddAttribute = () => {
    const current = formik.values.attributes || [];
    formik.setFieldValue("attributes", [
      ...current,
      { attributeName: "", attributeValue: "" },
    ]);
  };

  const handleRemoveAttribute = async (idx) => {
    const current = [...(formik.values.attributes || [])];
    const attr = current[idx];
    if (attr?.id) {
      try {
        await deleteVariantAttribute(attr.id);
      } catch {
        // si falla el API igual removemos del estado local
      }
    }
    current.splice(idx, 1);
    formik.setFieldValue("attributes", current);
  };

  const handleAttributeChange = (idx, field, value) => {
    const current = [...(formik.values.attributes || [])];
    current[idx] = { ...current[idx], [field]: value };
    formik.setFieldValue("attributes", current);
  };

  return (
    <Box sx={{ pt: 1 }}>
      <Grid container spacing={2}>

        {/* ─── VARIANTE ─── */}
        <Grid item xs={12}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#e11d48", mb: 1 }}>
            Variante del insumo
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <Autocomplete
            options={insumos || []}
            getOptionLabel={(option) =>
              option.codigo ? `${option.nombre} (${option.codigo})` : option.nombre || ""
            }
            filterOptions={filterInsumosOptions}
            isOptionEqualToValue={(opt, val) => opt.id === val.id}
            value={insumos?.find((i) => i.id === formik.values.insumoId) || null}
            onChange={(_, value) => {
              formik.setFieldValue("insumoId", value?.id || "");
              formik.setFieldValue("unit", value?.details?.presentacion || "");
              if (onInsumoChange) onInsumoChange(value);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Insumo *"
                error={formik.touched.insumoId && Boolean(formik.errors.insumoId)}
                helperText={formik.touched.insumoId && formik.errors.insumoId}
              />
            )}
          />
        </Grid>

        {/* ── Selector de variante existente (solo POST, cuando hay variantes del insumo) ── */}
        {mode === "POST" && existingVariants.length > 0 && (
          <Grid item xs={12}>
            <Alert severity="info" sx={{ mb: 1, fontFamily: "'Poppins', sans-serif", fontSize: "0.78rem" }}>
              Este insumo ya tiene {existingVariants.length} variante(s). Selecciona una para sumar stock, o deja vacío para crear una nueva.
            </Alert>
            <Autocomplete
              options={existingVariants}
              getOptionLabel={(v) => getVariantPrimaryLabel(v)}
              filterOptions={filterVariantsOptions}
              isOptionEqualToValue={(opt, val) => opt.id === val.id}
              value={selectedVariant || null}
              onChange={(_, v) => onVariantSelect(v)}
              clearOnEscape
              renderOption={(props, v) => (
                <li {...props} key={v.id}>
                  <Box>
                    <Typography sx={{ fontWeight: 600, fontSize: "0.82rem" }}>
                      {getVariantPrimaryLabel(v)}
                    </Typography>
                    <Typography sx={{ fontSize: "0.72rem", color: "#6b7280" }}>
                      SKU: {v.sku || "-"} &nbsp;·&nbsp;
                      Stock: {parseFloat(v.stock ?? 0)} &nbsp;·&nbsp;
                      Precio: ${parseFloat(v.price ?? 0).toFixed(2)}
                    </Typography>
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Variante existente (opcional)"
                  placeholder="Busca por color, medida o SKU..."
                  helperText={selectedVariant ? `SKU: ${selectedVariant.sku || "-"} · Stock base: ${parseFloat(selectedVariant.stock ?? 0)}` : "Sin selección → nueva variante"}
                />
              )}
            />
          </Grid>
        )}

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            id="sku"
            name="sku"
            label="SKU *"
            value={formik.values.sku}
            onChange={formik.handleChange}
            error={formik.touched.sku && Boolean(formik.errors.sku)}
            helperText={formik.touched.sku && formik.errors.sku || (mode === "POST" ? "Auto-generado del nombre del insumo" : undefined)}
          />
        </Grid>

        <Grid item xs={6} sm={3}>
          <TextField
            fullWidth
            id="variantStock"
            name="variantStock"
            label="Stock variante (resultado)"
            type="number"
            value={computedStock}
            disabled
            helperText={baseStock > 0 ? `Base actual: ${baseStock}` : "Se calcula automáticamente"}
            InputProps={{ readOnly: true }}
          />
        </Grid>

        <Grid item xs={6} sm={3}>
          <TextField
            fullWidth
            id="price"
            name="price"
            label="Precio"
            type="number"
            value={formik.values.price}
            onChange={formik.handleChange}
          />
        </Grid>

        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={Boolean(formik.values.active)}
                onChange={(e) => formik.setFieldValue("active", e.target.checked)}
                color="primary"
              />
            }
            label="Activo"
          />
        </Grid>

        {/* ─── ATRIBUTOS ─── */}
        <Grid item xs={12}>
          <Divider />
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 2, mb: 1.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#e11d48" }}>
              Atributos
            </Typography>
            <Button
              type="button"
              size="small"
              startIcon={<Add />}
              onClick={handleAddAttribute}
              sx={{
                color: "#f43f5e", borderColor: "#f43f5e",
                fontFamily: "'Poppins', sans-serif", fontWeight: 600, fontSize: "0.75rem",
              }}
              variant="outlined"
            >
              Agregar
            </Button>
          </Box>

          {(formik.values.attributes || []).length === 0 && (
            <Typography sx={{ fontSize: "0.8rem", color: "#9ca3af", fontStyle: "italic", mb: 1 }}>
              Sin atributos. Usa el botón para agregar.
            </Typography>
          )}

          {(formik.values.attributes || []).map((attr, idx) => (
            <Box
              key={idx}
              sx={{
                mb: 1.5, p: 1.5, borderRadius: 2,
                border: "1px solid rgba(244,63,94,0.18)",
                background: "rgba(253,242,244,0.5)",
                ".dark &": { background: "rgba(244,63,94,0.04)", borderColor: "rgba(244,63,94,0.2)" },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                <Chip
                  label={`Atributo ${idx + 1}`}
                  size="small"
                  sx={{
                    background: "rgba(244,63,94,0.1)", color: "#be185d",
                    fontFamily: "'Poppins', sans-serif", fontWeight: 600, fontSize: "0.7rem",
                  }}
                />
                <IconButton type="button" size="small" color="error" onClick={() => handleRemoveAttribute(idx)}>
                  <Delete fontSize="small" />
                </IconButton>
              </Box>
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={5}>
                  <Autocomplete
                    freeSolo
                    options={ATTRIBUTE_NAME_OPTIONS}
                    value={attr.attributeName || ""}
                    onInputChange={(_, newValue) =>
                      handleAttributeChange(idx, "attributeName", newValue || "")
                    }
                    renderInput={(params) => (
                      <TextField {...params} size="small" label="Nombre" fullWidth />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={7}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Valor"
                    value={attr.attributeValue}
                    onChange={(e) => handleAttributeChange(idx, "attributeValue", e.target.value)}
                  />
                </Grid>
              </Grid>
            </Box>
          ))}
        </Grid>

        {/* ─── MOVIMIENTO ─── */}
        <Grid item xs={12}>
          <Divider />
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#e11d48", mt: 2, mb: 1 }}>
            Movimiento
          </Typography>
        </Grid>

        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            id="movementType"
            name="movementType"
            label="Tipo *"
            select
            value={formik.values.movementType}
            onChange={formik.handleChange}
            error={formik.touched.movementType && Boolean(formik.errors.movementType)}
            helperText={formik.touched.movementType && formik.errors.movementType}
          >
            {MOVEMENT_TYPES.map((t) => (
              <MenuItem key={t} value={t}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Chip
                    label={t}
                    size="small"
                    color={MOVEMENT_COLORS[t] || "default"}
                    sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 600, fontSize: "0.72rem" }}
                  />
                  {t === "SALIDA" && (
                    <Typography sx={{ fontSize: "0.72rem", color: "#6b7280" }}>resta del stock</Typography>
                  )}
                  {t === "AJUSTE" && (
                    <Typography sx={{ fontSize: "0.72rem", color: "#6b7280" }}>reemplaza stock</Typography>
                  )}
                </Box>
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={6} sm={4}>
          <TextField
            fullWidth
            id="quantity"
            name="quantity"
            label="Cantidad *"
            type="number"
            value={formik.values.quantity}
            onChange={formik.handleChange}
            inputProps={{ step: "any" }}
            error={formik.touched.quantity && Boolean(formik.errors.quantity)}
            helperText={
              (formik.touched.quantity && formik.errors.quantity) ||
              (formik.values.movementType === "SALIDA"
                ? `Restará del stock actual: ${baseStock} → ${Math.max(0, baseStock - Math.abs(parseFloat(formik.values.quantity) || 0))}`
                : undefined)
            }
          />
        </Grid>

        <Grid item xs={6} sm={4}>
          <TextField
            fullWidth
            id="unit"
            name="unit"
            label="Unidad"
            value={formik.values.unit}
            disabled
            InputProps={{ readOnly: true }}
            helperText="Se obtiene del insumo seleccionado"
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            rows={2}
            id="notes"
            name="notes"
            label="Notas"
            value={formik.values.notes}
            onChange={formik.handleChange}
          />
        </Grid>

      </Grid>
    </Box>
  );
};

export default StackInsumosFormFields;

